import { useLanguage, useTranslation } from '@cobliteam/i18n';
import Progress from '@cobliteam/ui/components/Progress';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import { cn } from '@cobliteam/ui/utils';
import type { LanguageModelUsage } from 'ai';
import { memo } from 'react';
import { getUsage } from 'tokenlens';

const ICON_RADIUS = 10;
const ICON_VIEWBOX = 24;
const ICON_CENTER = 12;
const ICON_STROKE_WIDTH = 2;

const formatTokens = (n: number, locale: string) => new Intl.NumberFormat(locale, { notation: 'compact' }).format(n);

const formatCostUSD = (n: number, locale: string) => new Intl.NumberFormat(locale, { currency: 'USD', maximumFractionDigits: 4, style: 'currency' }).format(n);

type TokenRowProps = {
  label: string;
  tokens: number | undefined;
  costUSD?: number;
  locale: string;
};

const TokenRow = ({ label, tokens, costUSD, locale }: TokenRowProps) => {
  if (!tokens) return null;

  return (
    <div className='flex items-center justify-between gap-4'>
      <span className='text-gray-9'>{label}</span>
      <span className='font-mono'>
        {formatTokens(tokens, locale)}
        {costUSD === undefined ? null : <span className='ml-2 text-gray-9'>• {formatCostUSD(costUSD, locale)}</span>}
      </span>
    </div>
  );
};

type ContextUsageDetailProps = {
  usedTokens: number;
  maxTokens: number;
  usage?: LanguageModelUsage;
  modelId?: string;
};

const ContextUsageDetail = ({ usedTokens, maxTokens, usage, modelId }: ContextUsageDetailProps) => {
  const { t } = useTranslation('ai');
  const { currentLanguage: locale } = useLanguage();
  const usedPercent = Math.min(usedTokens / maxTokens, 1);
  const displayPct = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, style: 'percent' }).format(usedPercent);
  const usedFormatted = new Intl.NumberFormat(locale, { notation: 'compact' }).format(usedTokens);
  const totalFormatted = new Intl.NumberFormat(locale, { notation: 'compact' }).format(maxTokens);

  const inputTokens = usage?.inputTokens ?? 0;
  const outputTokens = usage?.outputTokens ?? 0;
  const reasoningTokens = usage?.outputTokenDetails?.reasoningTokens ?? usage?.reasoningTokens ?? 0;
  const cacheTokens = usage?.inputTokenDetails?.cacheReadTokens ?? usage?.cachedInputTokens ?? 0;

  let totalCostUSD: number | undefined;
  let inputCostUSD: number | undefined;
  let outputCostUSD: number | undefined;

  if (modelId && usage) {
    try {
      const costs = getUsage({ modelId, usage: { input: inputTokens, output: outputTokens } }).costUSD;
      totalCostUSD = costs?.totalUSD;
      inputCostUSD = costs?.inputUSD;
      outputCostUSD = costs?.outputUSD;
    } catch {
      // tokenlens doesn't recognize this model — skip costs
    }
  }

  const hasUsageRows = inputTokens > 0 || outputTokens > 0 || reasoningTokens > 0 || cacheTokens > 0;

  return (
    <div className='min-w-[220px] divide-y divide-gray-4 text-xs'>
      <div className='space-y-2 p-3'>
        <div className='flex items-center justify-between gap-3'>
          <span className='font-medium'>{displayPct}</span>
          <span className='font-mono text-gray-9'>{t('context_usage.usage_count', { total: totalFormatted, used: usedFormatted })}</span>
        </div>
        <Progress value={usedPercent * 100} size='sm' />
      </div>
      {hasUsageRows && (
        <div className='space-y-1.5 p-3'>
          <TokenRow label={t('context_usage.input')} locale={locale} tokens={inputTokens > 0 ? inputTokens : undefined} costUSD={inputCostUSD} />
          <TokenRow label={t('context_usage.output')} locale={locale} tokens={outputTokens > 0 ? outputTokens : undefined} costUSD={outputCostUSD} />
          <TokenRow label={t('context_usage.reasoning')} locale={locale} tokens={reasoningTokens > 0 ? reasoningTokens : undefined} />
          <TokenRow label={t('context_usage.cache')} locale={locale} tokens={cacheTokens > 0 ? cacheTokens : undefined} />
        </div>
      )}
      {totalCostUSD !== undefined && (
        <div className='flex items-center justify-between bg-gray-2 p-3'>
          <span className='text-gray-9'>{t('context_usage.total_cost')}</span>
          <span className='font-mono'>{formatCostUSD(totalCostUSD, locale)}</span>
        </div>
      )}
    </div>
  );
};

const ContextUsageIcon = ({ usedTokens, maxTokens, ariaLabel }: { usedTokens: number; maxTokens: number; ariaLabel: string }) => {
  const circumference = 2 * Math.PI * ICON_RADIUS;
  const dashOffset = circumference * (1 - Math.min(usedTokens / maxTokens, 1));

  return (
    <svg aria-label={ariaLabel} height='14' role='img' style={{ color: 'currentcolor' }} viewBox={`0 0 ${ICON_VIEWBOX} ${ICON_VIEWBOX}`} width='14'>
      <circle cx={ICON_CENTER} cy={ICON_CENTER} fill='none' opacity='0.25' r={ICON_RADIUS} stroke='currentColor' strokeWidth={ICON_STROKE_WIDTH} />
      <circle
        cx={ICON_CENTER}
        cy={ICON_CENTER}
        fill='none'
        opacity='0.7'
        r={ICON_RADIUS}
        stroke='currentColor'
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={dashOffset}
        strokeLinecap='round'
        strokeWidth={ICON_STROKE_WIDTH}
        style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
      />
    </svg>
  );
};

export type ContextUsageProps = ContextUsageDetailProps & { className?: string };

const ContextUsage = ({ usedTokens, maxTokens, usage, modelId, className }: ContextUsageProps) => {
  const { t } = useTranslation('ai');
  const { currentLanguage: locale } = useLanguage();
  const usedPercent = Math.min(usedTokens / maxTokens, 1);
  const renderedPercent = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, style: 'percent' }).format(usedPercent);

  return (
    <Tooltip
      className={cn('p-0', className)}
      content={<ContextUsageDetail maxTokens={maxTokens} modelId={modelId} usage={usage} usedTokens={usedTokens} />}
      position='top'
      size='lg'
    >
      <button
        aria-label={t('context_usage.aria_used', { percent: renderedPercent })}
        className='flex shrink-0 cursor-default items-center gap-1 text-gray-9 text-xs transition-colors hover:text-gray-11'
        type='button'
      >
        <span className='font-medium'>{renderedPercent}</span>
        <ContextUsageIcon ariaLabel={t('context_usage.icon_aria')} maxTokens={maxTokens} usedTokens={usedTokens} />
      </button>
    </Tooltip>
  );
};

export default memo(ContextUsage);
